/*
    Copyright (C) Paul B. Lucas 2025, 2026  ALL RIGHTS RESERVED
    Licensed to the University of Illinois Urbana Champaign

    This is a Javascript composable that invokes the agreements API, fetching and storing 
    agreements from the database.   Three methods are incuded:

        fetchAgrementsOnOrgs -      selects agreements based on a list of provided org codes
        fetchAgreementsOnQuery -    selects agreements based on a generalized query array of 
                                    key:value pairs.
        saveAgreement -             saves a single agreement to database
    

    The fetchs are async functions that awaits a results of an axios call to the API. When promise 
    is resolved, the results are returned in agreements ref.

    API configuration data is stored as JSON file in src/configuration/ and imported as a JSON array.  Import
    can do this from a basic JSON file.  Parameters in the config array include the base-url, the POSTQL ccf
    endpoint, and authentication data for the axios headers (API key).  Details:

        {
            "apim-key-name":    "Bearer",
            "apim-key":         "--- some key ---",
            "base-url":         "https://api.elam.fs.illinois.edu",
            "ccfURL":           "https://api.elam.fs.illinois.edu/ccf",
            "agreement-path":   "/agreements",
            "employee-path":    "/employees",
            "table-path":       "/tables",
            "assets-path":      "/assets",
            "nans":             "/nans",
            "image-url":        "/https://images.elam.fs.illinois.edu/employees/"     
        }     
        
    If the API URL changes in DNS, this file must be modified.


    USAGE:
    In calling code use the following:
    AgreementFunction(argument).then(() -> {
        let this.agreement = agreeResults.value[0][1]
    })


    <Revision History>
        02/25/2025      pblucas     created 
        03/16/2026      pblucas     Corrected typos & updated copyright
        
    </Revision History>
*/

import { ref } from 'vue'
import axios from 'axios'
import amApiConfigs from '@/configuration/amAPI.config.json'

const useAgreementsAPI = () =>    {

    // DO NOT USE DEFAULT FOR AUTH IF AXIOS IS ACCESSING MORE THAN ONE API ON A SINGLE INSTANCE
    const baseURL  = amApiConfigs['base-url']

    // set specific endpoint for agreements
    const URIAgreementPath  =  amApiConfigs['agreement-path'] + '/'
    const URIOrgs           =  'applicable-org/'
    const URIQS             =  "?v=2"
    //use the POSTQL endpoint 'ccf' for storing
    const ccfURL            =   amApiConfigs['ccfURL']

    // result reference to return agreements: array
    const agreeResults      =   ref([])           
    // reference for returned data on where data was stored (includes pkey, assigned uuid, and number)
    const agreeKeyData      =   ref({})
    // referenced for entire returned data on store multiple agreements
    const agreeStoreResponse =  ref({})

    const  fetchAgreementsOnQuery = async (aQuery)  =>  {

        var agreements = []
        // convert array of key:value pairs to / delimited components for  URL
        // e.g.,  ["first-name: "john", "last-name":"Doe"]  =>  /first-name/John/last-name/Doe
        let uri = Object.keys(aQuery).map((k) => { 
        return encodeURIComponent(k) + '/' + encodeURIComponent(aQuery[k])
        }).join('/')
        
        let url =  baseURL + URIAgreementPath + uri + URIQS     
        await axios({
            method:         'get',
            url:            url,
            headers:         {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
        })
        .then ( (response) => {   
            agreeResults.value      =   response.data.results     
            agreements              =   response.data.results  
        })
        .catch( error =>  {
            console.log("FetchAgreements:: "+ error.message)
        })
        return agreements
    }   // end function

    const  deleteAgreementsOnQuery = async (aQuery)  =>  {
        // convert array of key:value pairs to / delimited components for  URL
        // e.g.,  ["first-name: "john", "last-name":"Doe"]  =>  /first-name/John/last-name/Doe
        let uri = Object.keys(aQuery).map((k) => { 
        return encodeURIComponent(k) + '/' + encodeURIComponent(aQuery[k])
        }).join('/')
        
        let url =  baseURL + URIAgreementPath + uri     
        await axios({
            method:         'delete',
            url:            url,
            headers:         {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
        })
        .then ( (response) => {   
            agreeResults.value      =   response.data.response     
        })
        .catch( error =>  {
            console.log("DeleteAgreements:: "+ error.message)
        })
    }   // end function

    const  fetchAgreementsOnOrgs = async (aOrgs)  =>  {
    
        var agreements = []

        let resolvedPromises = await Promise.allSettled(aOrgs.map(org => {
            let url =  baseURL + URIAgreementPath + URIOrgs + org + URIQS    
            let data =  axios({
                method:         'get',
                url:            url,
                headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
            })
            return data
        }))
        // have an array of settled promised with either resolve or response callbacks
        resolvedPromises.forEach((rp, num)  =>  {

            //console.log("Returned promise: "+JSON.stringify(rp))
            
            if(rp.status == 'rejected') {
                console.log("Axios promise rejected: fetching orgcode: "+aOrgs[num])
                //throw Error ("Axios promise rejected: fetching orgcode: "+aOrgs[num])
            }
            else if(rp.status == 'fulfilled')  {
                // data available on promise
                const data = rp.value.data      
 
                // do some more detailed error checking on API return
                // data is JSON
                if((data.response['status'] == "failed") || (data.response['http_return'] != 200)) { 
                    //console.log("FetchAgreements:: error thrown in response")
                    //throw Error ("Unable to fetch agreement data")
                }
                if(data.results.length == 0) { 
                    //console.log("FetchAgreements:: no agreements found on org")
                    //throw Error ("No agreements found on orgcode "+ aOrgs[num])
                }
                // completed and valid query returns
                for (let k in data.results)  {
                
                    // get agreement object from API call returned results[]
                    let obj = data.results[k]    
                    agreements.push(obj)
                    // console.log("Pushing agreement: " + JSON.stringify(obj))    
                }
            }  
        })  
        agreeResults.value = agreements
    }

 
    // argument 'agreement' is a ref
    const storeAgreement =  async (agreement) => {

        // need to generate attributes data - JSON array
        const payload = JSON.stringify(agreement)

        let formData = new FormData()
        formData.append("collection","agreements")

        // we will update the agreement on a null query string using an POST method
        // and rely on the UPDATE ON DUPLICATE KEY (UUID) in the storeAgreements() database routine
        // 
        formData.append("action","POST")
        formData.append("query","{}")
        formData.append("attributes",payload)
        formData.append("parameters", "{}")
        try {
            await axios({
                method: 'POST',
                url: ccfURL,
                data: formData,
                headers: {'Content-Type': 'multipart/form-data',
                          'Authorization': `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
            })
            .then( (response) => {
                if (!response.status == 200)   {
                    throw Error ("Axios call failed")
                }
                //console.log("useSaveAgreement:: results: "+JSON.stringify(response.data.results))
                agreeKeyData.value = response.data                     
            })
        }
        catch(error)   {
            console.log("StoreAgreement:: error: "+error.message)
        }    
    }   


    // argument 'agreement' is an array of agreements
    const storeAllAgreements =  async (agreements) => {

        // need to generate attributes data - JSON array
        const payload = JSON.stringify(agreements)

        let formData = new FormData()
        formData.append("collection","agreements")

        // we will update the agreement on a null query string using an POST method
        // and rely on the UPDATE ON DUPLICATE KEY (UUID) in the storeAgreements() database routine
        // 
        formData.append("action","POST")
        formData.append("query","{}")
        formData.append("attributes",payload)
        formData.append("parameters", "{}")
        try {
            await axios({
                method: 'POST',
                url: ccfURL,
                data: formData,
                headers: {'Content-Type': 'multipart/form-data',
                          'Authorization': `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
            })
            .then( (response) => {
                if (!response.status == 200)   {
                    throw Error ("Axios call failed")
                }
                agreeStoreResponse.value = response.data                     
            })
        }
        catch(error)   {
            console.log("StoreAgreements:: error: "+error.message)
        }    
    }
    return {agreeResults, agreeKeyData, agreeStoreResponse, fetchAgreementsOnQuery, fetchAgreementsOnOrgs, 
            storeAgreement, storeAllAgreements, deleteAgreementsOnQuery}
}

export default useAgreementsAPI
